import { useEffect, useMemo, useState } from "react";
import { api, ron } from "@/lib/api";
import { formatDate } from "@/lib/date";
import { Link } from "react-router-dom";
import { TrendingUp, Wallet, Coins, Clock, Receipt, Trophy, CalendarDays, ArrowRight, Phone, Snowflake, FileBarChart, MapPin } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { EVENT_TYPE_MAP } from "@/lib/api";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

function Kpi({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 fade-up">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent || "bg-slate-100 text-slate-600"}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-[10px] uppercase tracking-widest text-slate-400">{label}</span>
      </div>
      <div className="font-display text-2xl tracking-tight font-semibold tabular" data-testid={`kpi-${label}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function PnLRow({ label, value, positive, negative, bold, large }) {
  const color = positive ? "text-emerald-600" : negative ? "text-rose-600" : "text-slate-700";
  return (
    <div className={`flex items-center justify-between ${bold ? "font-semibold" : ""}`}>
      <span className={`${bold ? "text-slate-900" : "text-slate-600"} ${large ? "text-base" : ""}`}>{label}</span>
      <span className={`tabular ${color} ${large ? "font-display text-2xl" : ""}`}>{ron(value)}</span>
    </div>
  );
}

const SOURCE_PALETTE = ["#0047FF", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#06B6D4", "#EF4444", "#64748B", "#84CC16", "#F97316"];

function SalesBySourceCard({ monthData = [], allData = [], monthLabel }) {
  const [scope, setScope] = useState("month"); // "month" | "all"
  const data = scope === "month" ? monthData : allData;
  const totalCount = useMemo(() => data.reduce((s, x) => s + (x.count || 0), 0), [data]);
  const totalRevenue = useMemo(() => data.reduce((s, x) => s + (x.revenue || 0), 0), [data]);
  const totalProfit = useMemo(() => data.reduce((s, x) => s + (x.profit || 0), 0), [data]);

  const chartData = data.map((d, i) => ({
    name: d.source,
    value: d.count,
    revenue: d.revenue,
    profit: d.profit,
    fill: SOURCE_PALETTE[i % SOURCE_PALETTE.length],
  }));

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="sales-by-source-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Performanță canal</div>
          <h2 className="font-display text-xl font-medium flex items-center gap-2">
            <MapPin className="w-5 h-5" />Vânzări pe platformă
          </h2>
        </div>
        <div className="inline-flex bg-slate-100 rounded-lg p-0.5 text-xs">
          <button
            data-testid="sbs-scope-month"
            onClick={() => setScope("month")}
            className={`px-3 py-1.5 rounded-md transition ${scope === "month" ? "bg-white shadow-sm font-medium" : "text-slate-500"}`}
          >
            Luna {monthLabel || "aceasta"}
          </button>
          <button
            data-testid="sbs-scope-all"
            onClick={() => setScope("all")}
            className={`px-3 py-1.5 rounded-md transition ${scope === "all" ? "bg-white shadow-sm font-medium" : "text-slate-500"}`}
          >
            Total
          </button>
        </div>
      </div>

      {data.length === 0 ? (
        <div className="text-sm text-slate-400 py-8 text-center">
          Nicio vânzare în această perioadă. Adaugă „de unde a văzut mașina" la marcarea vânzării ca să vezi performanța per platformă.
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6 items-center">
          {/* Donut chart */}
          <div className="relative h-64 w-full" data-testid="sbs-donut">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={95}
                  paddingAngle={2}
                  stroke="#fff"
                  strokeWidth={2}
                >
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, _name, props) => {
                    const d = props.payload;
                    return [`${value} vânz. · ${ron(d.revenue)} · profit ${ron(d.profit)}`, d.name];
                  }}
                  contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <div className="font-display text-2xl tabular font-semibold">{totalCount}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">vânzări</div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <table className="w-full text-sm" data-testid="sbs-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-400 text-left">
                  <th className="py-2 pl-2 font-medium">Platformă</th>
                  <th className="py-2 text-right font-medium">Nr.</th>
                  <th className="py-2 text-right font-medium">Venit</th>
                  <th className="py-2 text-right font-medium">Profit</th>
                  <th className="py-2 pr-2 text-right font-medium">%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.map((d, i) => {
                  const pct = totalCount > 0 ? (d.count / totalCount) * 100 : 0;
                  return (
                    <tr key={d.source} data-testid={`sbs-row-${d.source}`} className="hover:bg-slate-50">
                      <td className="py-2 pl-2">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: SOURCE_PALETTE[i % SOURCE_PALETTE.length] }} />
                          <span className="font-medium truncate">{d.source}</span>
                        </div>
                      </td>
                      <td className="py-2 text-right tabular">{d.count}</td>
                      <td className="py-2 text-right tabular text-slate-700">{ron(d.revenue)}</td>
                      <td className={`py-2 text-right tabular ${d.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{ron(d.profit)}</td>
                      <td className="py-2 pr-2 text-right tabular text-slate-500">{pct.toFixed(0)}%</td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-slate-200 font-semibold">
                  <td className="py-2 pl-2">Total</td>
                  <td className="py-2 text-right tabular">{totalCount}</td>
                  <td className="py-2 text-right tabular">{ron(totalRevenue)}</td>
                  <td className={`py-2 text-right tabular ${totalProfit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{ron(totalProfit)}</td>
                  <td className="py-2 pr-2 text-right tabular text-slate-500">100%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  async function load() {
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } catch (e) {}
  }

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("data:refresh", h);
    return () => window.removeEventListener("data:refresh", h);
  }, []);

  if (!data) return <div className="text-slate-400 text-sm">Se încarcă...</div>;

  return (
    <div className="space-y-6 sm:pl-52" data-testid="dashboard-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Privire de ansamblu</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium">Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Wallet} label="Stoc" value={ron(data.stock_value)} sub={`${data.stock_count} mașini`} accent="bg-blue-50 text-blue-700" />
        <Kpi icon={Coins} label="Investiție" value={ron(data.investment_total)} sub="capital blocat în stoc" accent="bg-amber-50 text-amber-700" />
        <Kpi icon={TrendingUp} label="Profit estimat" value={ron(data.profit_estimated)} sub="după marja la stoc" accent="bg-emerald-50 text-emerald-700" />
        <Kpi icon={Trophy} label="Vândute luna asta" value={data.sold_this_month} sub={ron(data.sold_this_month_revenue)} accent="bg-violet-50 text-violet-700" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Receipt} label="Costuri luna asta" value={ron(data.monthly_expenses)} sub={`pe mașini ${ron(data.car_expenses_month || 0)} · fixe ${ron(data.fixed_costs_month || 0)}`} accent="bg-rose-50 text-rose-700" />
        <Kpi icon={Clock} label="≥ 60 zile" value={data.stagnant_cars.length} sub="stagnante" accent="bg-slate-100 text-slate-700" />
        <Kpi icon={Snowflake} label="≥ 90 zile" value={data.stagnant_90_count || 0} sub="critice" accent="bg-red-50 text-red-700" />
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Lead-uri pe surse</div>
          {data.leads_by_source.length === 0 && <div className="text-xs text-slate-400">Niciun lead.</div>}
          <div className="space-y-1.5 mt-1">
            {data.leads_by_source.slice(0, 4).map((s) => (
              <div key={s.source} className="flex items-center gap-2 text-xs">
                <span className="w-16 truncate">{s.source}</span>
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#0047FF]" style={{ width: `${Math.min(100, (s.count / data.leads_by_source[0].count) * 100)}%` }} />
                </div>
                <span className="tabular text-slate-600 w-6 text-right">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Monthly P&L */}
      {data.pnl_month && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="pnl-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Raport luna {data.pnl_month.month_label}</div>
              <h2 className="font-display text-xl font-medium flex items-center gap-2"><FileBarChart className="w-5 h-5" />Profit & Loss</h2>
            </div>
            <span className="text-xs text-slate-500">{data.pnl_month.sales_count} vânzări</span>
          </div>
          <div className="space-y-2 text-sm">
            <PnLRow label="Vânzări" value={data.pnl_month.revenue} positive />
            <PnLRow label="Cost mașini vândute (achiziție + cheltuieli)" value={-data.pnl_month.cogs} negative />
            <PnLRow label="Profit brut" value={data.pnl_month.gross_profit} bold positive={data.pnl_month.gross_profit >= 0} />
            <div className="border-t border-dashed border-slate-200 my-2" />
            <PnLRow label="Cheltuieli mașini în stoc (reparații, transport...)" value={-(data.pnl_month.car_expenses_unsold || 0)} negative />
            <PnLRow label="Costuri fixe (chirie, salarii, leasing...)" value={-data.pnl_month.fixed_costs} negative />
            <div className="border-t border-slate-300 my-2" />
            <PnLRow label="Profit net" value={data.pnl_month.net_profit} bold large positive={data.pnl_month.net_profit >= 0} />
            <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
              ℹ️ Cheltuielile pe mașinile <strong>vândute luna asta</strong> sunt deja incluse în „Cost mașini vândute".
              Linia „Cheltuieli mașini în stoc" reflectă doar costurile aferente mașinilor încă în stoc (sau vândute în alte luni).
            </p>
          </div>
        </div>
      )}

      {/* Sales by source (canal de achiziție) */}
      <SalesBySourceCard
        monthData={data.sales_by_source_month || []}
        allData={data.sales_by_source_all || []}
        monthLabel={data.pnl_month?.month_label}
      />

      {/* Two-column on lg: stagnant + stale */}
      <div className="grid lg:grid-cols-2 gap-4">
        {data.stagnant_cars.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-600">Atenție</div>
                <h2 className="font-display text-lg font-medium">Mașini stagnante</h2>
              </div>
              <Link to="/cars" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1">Toate <ArrowRight className="w-3 h-3" /></Link>
            </div>
            <div className="space-y-2">
              {data.stagnant_cars.slice(0, 5).map((c) => (
                <Link key={c.id} to={`/cars/${c.id}`} data-testid={`stagnant-car-${c.id}`} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-2 px-2 rounded">
                  <div>
                    <div className="font-medium text-sm">{c.make} {c.model}</div>
                    <div className="text-xs text-slate-500">{c.vin?.slice(-6) || c.year || "—"}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={c.status} />
                    <span className={`text-xs font-mono ${c.days_in_stock >= 90 ? "text-red-600" : "text-amber-600"}`}>{c.days_in_stock}z</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {(data.stale_clients || []).length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="stale-clients-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-rose-600">Follow-up urgent</div>
                <h2 className="font-display text-lg font-medium">Clienți reci ({">"}7 zile)</h2>
              </div>
              <Link to="/clients" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1">Toți <ArrowRight className="w-3 h-3" /></Link>
            </div>
            <div className="space-y-2">
              {data.stale_clients.map((c) => {
                const days = Math.floor((Date.now() - new Date(c.last_contact).getTime()) / 86400000);
                return (
                  <Link key={c.id} to={`/clients/${c.id}`} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-2 px-2 rounded" data-testid={`stale-client-${c.id}`}>
                    <div>
                      <div className="font-medium text-sm">{c.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1.5">
                        {c.phone && <><Phone className="w-3 h-3" />{c.phone}</>}
                        {c.source && <span className="ml-1 px-1.5 py-0.5 bg-slate-100 rounded">{c.source}</span>}
                      </div>
                    </div>
                    <span className="text-xs font-mono text-rose-600">{days}z</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Upcoming events */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Activități</div>
            <h2 className="font-display text-lg font-medium flex items-center gap-2"><CalendarDays className="w-4 h-4" />Evenimente apropiate</h2>
          </div>
          <Link to="/calendar" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1">Calendar <ArrowRight className="w-3 h-3" /></Link>
        </div>
        {(data.upcoming_events || []).length === 0 && <div className="text-sm text-slate-400">Niciun eveniment programat.</div>}
        <div className="space-y-2">
          {(data.upcoming_events || []).map((ev) => {
            const t = EVENT_TYPE_MAP[ev.event_type] || EVENT_TYPE_MAP.custom;
            const dt = new Date(ev.start_datetime);
            return (
              <Link
                key={ev.id}
                to="/calendar"
                data-testid={`upcoming-event-${ev.id}`}
                className={`flex justify-between items-center py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-2 px-2 rounded ${ev.overdue ? "text-red-600" : ""}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center text-sm shrink-0" style={{ backgroundColor: t.color + "22", color: t.color }}>
                    {t.emoji}
                  </span>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{ev.title}</div>
                    <div className="text-xs text-slate-500">
                      {formatDate(dt)} · {ev.all_day ? "toată ziua" : dt.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}
                      {ev.event_type !== "custom" ? ` · ${t.label}` : ""}
                    </div>
                  </div>
                </div>
                {ev.overdue && <span className="text-[10px] uppercase tracking-widest bg-red-50 text-red-600 px-2 py-0.5 rounded-full shrink-0">Întârziat</span>}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
